/** Merge these keys under presentations.copyReview in each locale. */
export const copyReviewCopy = {
  en: {
    eyebrow: 'Copy to another Project',
    title: 'Review copy proposal',
    back: 'Back to presentation',
    retry: 'Try again',
    loadErrorTitle: 'This proposal is unavailable',
    loadError: 'It may not exist, or you may not have access to it.',
    proposedBy: 'Prepared by {{who}}',
    you: 'you',
    anotherPerson: 'another person',
    expires: 'Expires {{date}}',
    status: {
      PENDING_REVIEW: 'Pending review',
      EXPIRED: 'Expired',
      REJECTED: 'Rejected',
      EXECUTED: 'Copied',
    },
    sections: {
      operation: 'What will happen',
      source: 'Source',
      destination: 'Destination',
      classification: 'Classification and data exposure',
      templates: 'Templates',
      assets: 'Files',
      appearance: 'Appearance',
      copied: 'Copied',
      omitted: 'Not copied',
      findings: 'Blockers and warnings',
    },
    operation:
      'Approving creates a new draft presentation named “{{name}}” in “{{project}}”. The original presentation stays unchanged and nothing is published or shared.',
    notes:
      'Presenter notes and scripts are preserved because you can edit the source. Their text is not shown here.',
    narration: 'Generated narration audio is not copied; no speech is generated.',
    sourceLine: '“{{name}}” · revision {{revision}}',
    destinationLine: '“{{name}}” · revision {{revision}}',
    slides: '{{count}} slides',
    proposedName: 'New presentation name',
    classificationLine: 'Source {{from}} → destination {{to}}',
    classificationLevel: {
      NON_SENSITIVE: 'Non-sensitive',
      INTERNAL: 'Internal',
      CONFIDENTIAL: 'Confidential',
    },
    provider:
      'Data exposure to AI providers cannot grow: the destination is at least as restricted, and this is checked again when you approve.',
    templatesLine: '{{ok}} of {{total}} template versions are allowed in the destination.',
    designKit: 'The destination Project restricts templates to its Design Kit.',
    assetsLine: '{{count}} file replacement(s) chosen.',
    noAssets: 'No file replacements are needed.',
    inaccessible: '{{count}} file(s) are not available to you.',
    appearance:
      'The destination Project’s theme applies. Inherited appearance may look different from the original.',
    noBlockers: 'No blockers were found when this proposal was prepared.',
    staleNote:
      'These findings are what was prepared. Everything is checked again, atomically, when you approve.',
    decision: {
      label: 'Decision',
      approve: 'Approve and copy',
      approving: 'Copying…',
      reject: 'Reject',
      rejecting: 'Rejecting…',
      rejectHint: 'Rejection is permanent. Prepare a new proposal to try again.',
      onlyRequester:
        'Only the person who prepared this proposal can approve or reject it. Another Owner cannot decide on their behalf.',
      gated:
        'Approving is switched off for this workspace until the owner accepts the copy policy. You can still reject.',
      blocked: 'Resolve the blockers by preparing a new proposal.',
      expired: 'This proposal expired. Prepare a new one to copy.',
      hint: 'Approving creates one draft copy. Review everything above first.',
    },
    result: {
      title: 'Copied as a draft',
      body: 'The new draft presentation was created.',
      open: 'Open the new draft',
    },
    rejected: {
      title: 'Proposal rejected',
      body: 'Nothing was copied. This decision is permanent.',
    },
    expiredBanner: {
      title: 'Proposal expired',
      body: 'Nothing was copied. Prepare a new proposal from the presentation.',
    },
    unknownOutcome:
      'The connection dropped before we got an answer. The current status was reloaded; check it before trying again.',
    errors: {
      default: 'The decision could not be completed.',
      COPY_EXECUTION_DISABLED:
        'Copying between Projects is not enabled for this workspace yet. You can still reject this proposal.',
      COPY_PROPOSAL_EXPIRED: 'This proposal expired. Prepare a new one.',
      COPY_PROPOSAL_STALE:
        'The presentation, Project or policy changed since this was prepared. Prepare a new proposal.',
      COPY_PROPOSAL_ALREADY_DECIDED:
        'This proposal was already decided. The current status is shown.',
      COPY_PROPOSAL_NOT_FOUND: 'This proposal is unavailable.',
      VALIDATION_FAILED: 'The decision was not valid. Reload and try again.',
      ACCESS_DENIED: 'You no longer have permission to decide this proposal.',
      CSRF_TOKEN_INVALID: 'Your session needs to be refreshed. Reload the page and try again.',
      UNAUTHENTICATED: 'Your session ended. Sign in again to continue.',
    },
    properties: {
      slideOrder: 'Slide order',
      chapters: 'Chapters',
      structuredContent: 'Content',
      settings: 'Slide settings',
      visibility: 'Visibility',
      narrationScripts: 'Narration scripts',
      presenterCues: 'Presenter cues',
      textScale: 'Text scale',
      appearanceOverrides: 'Appearance overrides',
      variables: 'Variables',
      exactTemplateVersions: 'Exact template versions',
      explicitlyValidatedAssetReferences: 'Validated file references',
      presenterNotes: 'Presenter notes',
      presenterScripts: 'Presenter scripts',
      releases: 'Published releases',
      shareLinks: 'Share links',
      analytics: 'Analytics',
      accessGrants: 'Access grants',
      generatedNarrationAudio: 'Generated narration audio',
      storageObjects: 'Stored media objects',
    },
  },
  fa: {
    eyebrow: 'کپی به پروژه دیگر',
    title: 'بازبینی پیشنهاد کپی',
    back: 'بازگشت به ارائه',
    retry: 'تلاش دوباره',
    loadErrorTitle: 'این پیشنهاد در دسترس نیست',
    loadError: 'ممکن است وجود نداشته باشد یا به آن دسترسی نداشته باشید.',
    proposedBy: 'آماده‌شده توسط {{who}}',
    you: 'شما',
    anotherPerson: 'شخص دیگری',
    expires: 'انقضا: {{date}}',
    status: {
      PENDING_REVIEW: 'در انتظار بازبینی',
      EXPIRED: 'منقضی‌شده',
      REJECTED: 'ردشده',
      EXECUTED: 'کپی‌شده',
    },
    sections: {
      operation: 'چه اتفاقی می‌افتد',
      source: 'مبدا',
      destination: 'مقصد',
      classification: 'طبقه‌بندی و در معرض قرار گرفتن داده',
      templates: 'قالب‌ها',
      assets: 'فایل‌ها',
      appearance: 'ظاهر',
      copied: 'کپی می‌شود',
      omitted: 'کپی نمی‌شود',
      findings: 'موانع و هشدارها',
    },
    operation:
      'با تأیید، یک پیش‌نویس جدید با نام «{{name}}» در «{{project}}» ساخته می‌شود. ارائه اصلی بدون تغییر می‌ماند و چیزی منتشر یا به اشتراک گذاشته نمی‌شود.',
    notes:
      'یادداشت‌ها و متن‌های ارائه‌دهنده چون شما می‌توانید مبدا را ویرایش کنید حفظ می‌شوند. متن آن‌ها اینجا نمایش داده نمی‌شود.',
    narration: 'صدای روایت تولیدشده کپی نمی‌شود و گفتاری تولید نمی‌شود.',
    sourceLine: '«{{name}}» · نسخه {{revision}}',
    destinationLine: '«{{name}}» · نسخه {{revision}}',
    slides: '{{count}} اسلاید',
    proposedName: 'نام ارائه جدید',
    classificationLine: 'مبدا {{from}} ← مقصد {{to}}',
    classificationLevel: {
      NON_SENSITIVE: 'غیرحساس',
      INTERNAL: 'داخلی',
      CONFIDENTIAL: 'محرمانه',
    },
    provider:
      'در معرض قرار گرفتن داده برای ارائه‌دهندگان هوش مصنوعی افزایش نمی‌یابد: مقصد دست‌کم به‌اندازه مبدا محدود است و هنگام تأیید دوباره بررسی می‌شود.',
    templatesLine: '{{ok}} از {{total}} نسخه قالب در مقصد مجاز است.',
    designKit: 'پروژه مقصد قالب‌ها را به کیت طراحی خود محدود کرده است.',
    assetsLine: '{{count}} جایگزین فایل انتخاب شده است.',
    noAssets: 'جایگزینی فایل لازم نیست.',
    inaccessible: '{{count}} فایل برای شما در دسترس نیست.',
    appearance: 'تم پروژه مقصد اعمال می‌شود. ظاهر ارث‌بری‌شده ممکن است با نسخه اصلی متفاوت باشد.',
    noBlockers: 'هنگام آماده‌سازی این پیشنهاد مانعی پیدا نشد.',
    staleNote:
      'این یافته‌ها همان چیزی است که آماده شده بود. هنگام تأیید همه‌چیز دوباره و به‌صورت یکپارچه بررسی می‌شود.',
    decision: {
      label: 'تصمیم',
      approve: 'تأیید و کپی',
      approving: 'در حال کپی…',
      reject: 'رد',
      rejecting: 'در حال رد…',
      rejectHint: 'رد کردن دائمی است. برای تلاش دوباره پیشنهاد جدیدی بسازید.',
      onlyRequester:
        'فقط کسی که این پیشنهاد را آماده کرده می‌تواند آن را تأیید یا رد کند. مالک دیگر نمی‌تواند به‌جای او تصمیم بگیرد.',
      gated:
        'تأیید برای این فضای کاری تا پذیرش سیاست کپی توسط مالک غیرفعال است. هنوز می‌توانید رد کنید.',
      blocked: 'با ساخت پیشنهاد جدید موانع را برطرف کنید.',
      expired: 'این پیشنهاد منقضی شد. برای کپی پیشنهاد جدیدی بسازید.',
      hint: 'تأیید یک نسخه پیش‌نویس می‌سازد. ابتدا همه موارد بالا را بررسی کنید.',
    },
    result: {
      title: 'به‌صورت پیش‌نویس کپی شد',
      body: 'ارائه پیش‌نویس جدید ساخته شد.',
      open: 'باز کردن پیش‌نویس جدید',
    },
    rejected: {
      title: 'پیشنهاد رد شد',
      body: 'چیزی کپی نشد. این تصمیم دائمی است.',
    },
    expiredBanner: {
      title: 'پیشنهاد منقضی شد',
      body: 'چیزی کپی نشد. از ارائه یک پیشنهاد جدید بسازید.',
    },
    unknownOutcome:
      'اتصال پیش از دریافت پاسخ قطع شد. وضعیت فعلی دوباره بارگیری شد؛ پیش از تلاش دوباره آن را بررسی کنید.',
    errors: {
      default: 'تصمیم نهایی نشد.',
      COPY_EXECUTION_DISABLED:
        'کپی بین پروژه‌ها هنوز برای این فضای کاری فعال نشده است. هنوز می‌توانید این پیشنهاد را رد کنید.',
      COPY_PROPOSAL_EXPIRED: 'این پیشنهاد منقضی شد. پیشنهاد جدیدی بسازید.',
      COPY_PROPOSAL_STALE:
        'ارائه، پروژه یا سیاست پس از آماده‌سازی تغییر کرده است. پیشنهاد جدیدی بسازید.',
      COPY_PROPOSAL_ALREADY_DECIDED:
        'این پیشنهاد قبلاً تصمیم‌گیری شده است. وضعیت فعلی نمایش داده می‌شود.',
      COPY_PROPOSAL_NOT_FOUND: 'این پیشنهاد در دسترس نیست.',
      VALIDATION_FAILED: 'تصمیم معتبر نبود. صفحه را بارگیری کنید و دوباره تلاش کنید.',
      ACCESS_DENIED: 'دیگر مجوز تصمیم‌گیری درباره این پیشنهاد را ندارید.',
      CSRF_TOKEN_INVALID: 'نشست شما باید تازه شود. صفحه را بارگیری کنید و دوباره تلاش کنید.',
      UNAUTHENTICATED: 'نشست شما پایان یافت. برای ادامه دوباره وارد شوید.',
    },
    properties: {
      slideOrder: 'ترتیب اسلایدها',
      chapters: 'فصل‌ها',
      structuredContent: 'محتوا',
      settings: 'تنظیمات اسلاید',
      visibility: 'نمایان بودن',
      narrationScripts: 'متن روایت',
      presenterCues: 'نشانه‌های ارائه‌دهنده',
      textScale: 'اندازه متن',
      appearanceOverrides: 'جایگزینی‌های ظاهر',
      variables: 'متغیرها',
      exactTemplateVersions: 'نسخه‌های دقیق قالب',
      explicitlyValidatedAssetReferences: 'ارجاع‌های تأییدشده فایل',
      presenterNotes: 'یادداشت‌های ارائه‌دهنده',
      presenterScripts: 'متن‌های ارائه‌دهنده',
      releases: 'انتشارهای منتشرشده',
      shareLinks: 'پیوندهای اشتراک',
      analytics: 'آمار',
      accessGrants: 'دسترسی‌ها',
      generatedNarrationAudio: 'صدای روایت تولیدشده',
      storageObjects: 'اشیای رسانه ذخیره‌شده',
    },
  },
} as const;
